<script setup lang="ts">
import { Button, Card, Icon, IconButton } from "@/design-system";
import type { Strings } from "@/domain/i18n";
import { useInstallPrompt } from "@/composables/useInstallPrompt";

/**
 * Add-to-home-screen banner.
 *
 * Not from the design handoff -- there is no spec for it, so it is built from
 * existing primitives and existing glyphs only. `plus` and `x` are both in the
 * twenty-glyph inventory; inventing an icon would have broken the rule that
 * nothing outside that set ships.
 *
 * It sits at the bottom of the page rather than over the content: this is an
 * offer, not an interruption, and the fuelling screens are the reason people
 * opened the app.
 */
defineProps<{ t: Strings }>();

const { visible, canPrompt, ios, install, dismiss } = useInstallPrompt();
</script>

<template>
  <Card
    v-if="visible"
    variant="sunken"
    class="install"
    data-testid="install-prompt"
  >
    <div class="install__row">
      <Icon
        name="plus"
        :size="20"
        color="var(--clay-500)"
      />
      <div class="install__text">
        <p class="install__title">
          {{ t.installTitle }}
        </p>
        <p class="install__body">
          {{ ios && !canPrompt ? t.installIos : t.installBody }}
        </p>
      </div>
      <IconButton
        icon="x"
        size="sm"
        :label="t.installDismiss"
        data-testid="install-dismiss"
        @click="dismiss"
      />
    </div>

    <!-- iOS has no programmatic install, so it gets instructions above and no
         button here -- a button that cannot do anything is worse than none. -->
    <Button
      v-if="canPrompt"
      size="sm"
      icon="plus"
      class="install__cta"
      data-testid="install-cta"
      @click="install"
    >
      {{ t.installCta }}
    </Button>
  </Card>
</template>

<style scoped>
.install {
  margin-top: var(--space-6);
}
.install__row {
  display: flex;
  align-items: flex-start;
  gap: var(--space-5);
}
.install__text {
  flex: 1;
  min-width: 0;
}
.install__title {
  margin: 0;
  font-size: var(--text-base);
  font-weight: var(--weight-semibold);
  color: var(--text-strong);
}
.install__body {
  margin: var(--space-2) 0 0;
  font: var(--type-body-sm);
  color: var(--text-muted);
}
.install__cta {
  margin-top: var(--space-5);
}
</style>
